/**
 * Persistance locale du générateur de facture.
 *
 * Aucune API serveur n'existe pour les factures : tout vit dans le localStorage
 * du navigateur. Chaque accès est protégé — navigation privée, quota dépassé ou
 * stockage désactivé par l'entreprise ne doivent jamais casser l'écran.
 */

import {normaliser} from './modele'
import {normaliserMontant} from './calculs'

export const CLES = {
  brouillon: 'abft_facture_brouillon',
  historique: 'abft_facture_historique',
  memos: 'abft_facture_memos',
  carnet: 'abft_facture_carnet',
  compteurs: 'abft_facture_compteur'
}

export const PLAFOND_HISTORIQUE = 50
export const PLAFOND_MEMOS = 100
export const PLAFOND_CARNET = 50

function lire (cle, defaut) {
  try {
    const brut = window.localStorage.getItem(cle)
    if (!brut) return defaut
    const valeur = JSON.parse(brut)
    return valeur === null || valeur === undefined ? defaut : valeur
  } catch (erreur) {
    return defaut
  }
}

function ecrire (cle, valeur) {
  try {
    window.localStorage.setItem(cle, JSON.stringify(valeur))
    return true
  } catch (erreur) {
    return false
  }
}

function supprimer (cle) {
  try {
    window.localStorage.removeItem(cle)
    return true
  } catch (erreur) {
    return false
  }
}

/**
 * Test d'écriture réel : la simple présence de window.localStorage ne dit pas
 * si l'écriture passera (Safari en navigation privée lève à l'écriture).
 */
export function stockageDisponible () {
  try {
    const temoin = '__abft_test__'
    window.localStorage.setItem(temoin, '1')
    window.localStorage.removeItem(temoin)
    return true
  } catch (erreur) {
    return false
  }
}

/* Brouillon en cours */

export function lireBrouillon () {
  const brut = lire(CLES.brouillon, null)
  return brut ? normaliser(brut) : null
}

export function ecrireBrouillon (facture) {
  return ecrire(CLES.brouillon, facture)
}

export function effacerBrouillon () {
  return supprimer(CLES.brouillon)
}

/* Historique des factures enregistrées */

export function lireHistorique () {
  const liste = lire(CLES.historique, [])
  return Array.isArray(liste) ? liste : []
}

export function ecrireHistorique (liste) {
  return ecrire(CLES.historique, liste.slice(0, PLAFOND_HISTORIQUE))
}

/**
 * Enregistre ou met à jour une facture. Les plus récentes d'abord : c'est
 * l'ordre dans lequel l'historique est affiché et le plus ancien qui tombe
 * quand le plafond est atteint.
 */
export function enregistrerDansHistorique (facture) {
  const liste = lireHistorique().filter(function (entree) {
    return entree.id !== facture.id
  })
  liste.unshift(facture)
  const conservee = liste.slice(0, PLAFOND_HISTORIQUE)
  return ecrireHistorique(conservee) ? conservee : liste
}

export function supprimerDeHistorique (id) {
  const liste = lireHistorique().filter(function (entree) {
    return entree.id !== id
  })
  ecrireHistorique(liste)
  return liste
}

/* Mémos de descriptions et de prix */

export function lireMemos () {
  const liste = lire(CLES.memos, [])
  return Array.isArray(liste) ? liste : []
}

/**
 * Mémorise les libellés facturés avec leur dernier prix. Les plus récemment
 * utilisés remontent : les suggestions restent pertinentes dans le temps.
 */
export function memoriserLignes (lignes) {
  const memos = lireMemos()
  const maintenant = Date.now()

  ;(lignes || []).forEach(function (ligne) {
    const description = String(ligne.description || '').trim()
    const prix = normaliserMontant(ligne.prixUnitaire)
    if (!description || !prix) return

    const existant = memos.filter(function (memo) {
      return memo.description === description
    })[0]

    if (existant) {
      existant.prixUnitaire = prix
      existant.utiliseLe = maintenant
    } else {
      memos.push({description: description, prixUnitaire: prix, utiliseLe: maintenant})
    }
  })

  memos.sort(function (a, b) {
    return (b.utiliseLe || 0) - (a.utiliseLe || 0)
  })

  const conserves = memos.slice(0, PLAFOND_MEMOS)
  ecrire(CLES.memos, conserves)
  return conserves
}

/* Carnet d'adresses local */

export function lireCarnet () {
  const liste = lire(CLES.carnet, [])
  return Array.isArray(liste) ? liste : []
}

export function ajouterAuCarnet (client) {
  const nom = String((client && client.nom) || '').trim()
  if (!nom) return lireCarnet()

  const liste = lireCarnet().filter(function (entree) {
    return String(entree.nom || '').trim().toLowerCase() !== nom.toLowerCase()
  })
  liste.unshift({
    nom: nom,
    adresse: client.adresse || '',
    cp: client.cp || '',
    ville: client.ville || '',
    pays: client.pays || 'Belgique',
    email: client.email || '',
    numEntreprise: client.numEntreprise || ''
  })
  const conserves = liste.slice(0, PLAFOND_CARNET)
  ecrire(CLES.carnet, conserves)
  return conserves
}

export function retirerDuCarnet (index) {
  const liste = lireCarnet()
  liste.splice(index, 1)
  ecrire(CLES.carnet, liste)
  return liste
}

/* Compteur de numérotation */

export function lireCompteurs () {
  const compteurs = lire(CLES.compteurs, {})
  return compteurs && typeof compteurs === 'object' ? compteurs : {}
}

export function ecrireCompteurs (compteurs) {
  return ecrire(CLES.compteurs, compteurs)
}

export default {
  CLES,
  stockageDisponible,
  lireBrouillon,
  ecrireBrouillon,
  effacerBrouillon,
  lireHistorique,
  enregistrerDansHistorique,
  supprimerDeHistorique,
  lireMemos,
  memoriserLignes,
  lireCarnet,
  ajouterAuCarnet,
  retirerDuCarnet,
  lireCompteurs,
  ecrireCompteurs
}



// WEBPACK FOOTER //
// ./src/utils/facture/stockage.js